import React, { useState, useRef, useEffect } from "react";
import styles from "./ContactUsWithWarehouses.module.scss";
import { useFormik } from "formik";
import ElchemyLogoSVG from "../ContactUsForm/ElchemyLogoSVG";
import AddressSVG from "./AddressSVG";
import MobileNumberSVG from "./MobileNumberSVG";
import Image from "next/image";
import Link from "next/link";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import ReCAPTCHA from "react-google-recaptcha";
import { submitContactForm } from "../../../api/FormsApi";
import { getCDNImageUrl } from "@/utils/Common";
import { usePathname } from "next/navigation";
import {
  trackFormLoaded,
  trackFormSubmitted,
  FORM_TYPES,
  getPageType,
} from "@/utils/gtmEvents";
import {
  showToast,
  TOAST_TYPE,
  FORM_TOAST_CONTAINER_ID,
} from "@/components/Common/Toast";

/**
 * App-wide form-toast container id. The single `<Toaster>` for it lives in
 * `components/Layout.jsx` — see `components/Common/Toast/constants.js` for why it
 * is mounted there and not per-form.
 */
const TOAST_CONTAINER_ID = FORM_TOAST_CONTAINER_ID;

// Common country codes list
const countryCodes = [
  { code: "+1", country: "US", flag: "🇺🇸" },
  { code: "+1", country: "CA", flag: "🇨🇦" },
  { code: "+44", country: "GB", flag: "🇬🇧" },
  { code: "+91", country: "IN", flag: "🇮🇳" },
  { code: "+86", country: "CN", flag: "🇨🇳" },
  { code: "+49", country: "DE", flag: "🇩🇪" },
  { code: "+33", country: "FR", flag: "🇫🇷" },
  { code: "+81", country: "JP", flag: "🇯🇵" },
  { code: "+82", country: "KR", flag: "🇰🇷" },
  { code: "+61", country: "AU", flag: "🇦🇺" },
  { code: "+7", country: "RU", flag: "🇷🇺" },
  { code: "+55", country: "BR", flag: "🇧🇷" },
  { code: "+34", country: "ES", flag: "🇪🇸" },
  { code: "+39", country: "IT", flag: "🇮🇹" },
  { code: "+31", country: "NL", flag: "🇳🇱" },
  { code: "+46", country: "SE", flag: "🇸🇪" },
  { code: "+41", country: "CH", flag: "🇨🇭" },
  { code: "+65", country: "SG", flag: "🇸🇬" },
  { code: "+852", country: "HK", flag: "🇭🇰" },
  { code: "+971", country: "AE", flag: "🇦🇪" },
];

// Validation schema (copied from ContactUsForm.jsx)
const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required"),
  email: Yup.string()
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      "Enter a valid email address"
    )
    .required("Email is required"),
  mobileNumber: Yup.string().typeError(
    "That doesn't look like a phone number"
  ),
  message: Yup.string()
    .trim()
    .required("Message is required")
    .max(500, "Message exceeds 500 characters"),
  recaptcha: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
    ? Yup.string().required("Please complete the reCAPTCHA")
    : Yup.string().nullable(),
  termsAccepted: Yup.boolean().oneOf(
    [true],
    "You must agree to the Terms & Conditions"
  ),
});

function ContactUsWithWarehouses() {
  const [selectedTab, setSelectedTab] = useState("Office");
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef({});
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pathname = usePathname();

  // Track FORM_LOADED event on mount
  useEffect(() => {
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const pageType = getPageType(pathname);
    trackFormLoaded(FORM_TYPES.CONTACT_US, pageUrl, pageType);
  }, [pathname]);

  const tabs = [
    { id: "office", label: "Office", value: "Office" },
    {
      id: "warehouse1",
      label: "Primary Warehouse",
      value: "Warehouse 1",
    },
    // {
    //   id: "warehouse2",
    //   label: "Pleasantville Dr, Houston",
    //   value: "Warehouse 2",
    // },
    // { id: "warehouse3", label: "Warehouse 3", value: "Warehouse 3" },
  ];

  const updateIndicatorPosition = (tabValue) => {
    const selectedTabData = tabs.find((tab) => tab.value === tabValue);
    if (selectedTabData && tabRefs.current[selectedTabData.id]) {
      const tabElement = tabRefs.current[selectedTabData.id];
      const { offsetLeft, offsetWidth } = tabElement;

      setIndicatorStyle({
        transform: `translateX(${offsetLeft}px)`,
        width: `${offsetWidth}px`,
      });
    }
  };

  useEffect(() => {
    // Update indicator position when component mounts or selectedTab changes
    updateIndicatorPosition(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      updateIndicatorPosition(selectedTab);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedTab]);

  const handleTabClick = (tabValue) => {
    setSelectedTab(tabValue);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobileNumber: "",
      message: "",
      recaptcha: null,
      termsAccepted: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const formData = {
          name: values.name,
          email: values.email,
          mobileNumber: values.mobileNumber,
          country: "United States", // Default for now, can be extracted from phone input later
          message: values.message,
          recaptcha: values.recaptcha,
        };
        const result = await submitContactForm(formData);
        if (result) {
          // Track FORM_SUBMITTED event
          const pageUrl = typeof window !== "undefined" ? window.location.href : "";
          const pageType = getPageType(pathname);
          trackFormSubmitted(FORM_TYPES.CONTACT_US, pageUrl, pageType);

          formik.resetForm();
          window?.grecaptcha?.reset();
          showToast("Request Submitted, We will get back soon!", {
            type: TOAST_TYPE.SUCCESS,
            containerId: TOAST_CONTAINER_ID,
          });
        } else {
          showToast("Something went wrong, please try again!", {
            type: TOAST_TYPE.ERROR,
            containerId: TOAST_CONTAINER_ID,
          });
        }
      } catch (error) {
        showToast("Something went wrong, please try again!", {
          type: TOAST_TYPE.ERROR,
          containerId: TOAST_CONTAINER_ID,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handlePhoneFocus = () => {
    setIsPhoneFocused(true);
  };

  const handlePhoneBlur = () => {
    setIsPhoneFocused(false);
    formik.handleBlur("mobileNumber");
  };

  return (
    <div className={styles.contactUsWithWarehouses}>
      <div className={styles.topLeftGradient} />
      <div className={styles.topRightGradient} />
      <div className={styles.bottomLeftGradient} />
      {/* Container */}
      <div className={styles.container}>
        {/* Form Container */}
        <div className={styles.formContainer}>
          {/* Heading */}
          <div className={styles.heading}>Any question or remarks?</div>
          {/* Sub Heading */}
          <div className={styles.subHeading}>Just write us a message!</div>
          {/* Form */}
          <form onSubmit={formik.handleSubmit} className={styles.form}>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <div className={styles.errorMessage}>{formik.errors.name}</div>
            )}

            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <div className={styles.errorMessage}>{formik.errors.email}</div>
            )}

            {/* Phone Input */}
            <div
              className={`${styles.phoneInputContainer} ${
                isPhoneFocused ? styles.phoneFocused : ""
              }`}
            >
              <PhoneInput
                country={"us"}
                enableSearch={true}
                value={formik.values.mobileNumber}
                onChange={(value) => {
                  formik.setFieldValue("mobileNumber", value);
                }}
                onFocus={handlePhoneFocus}
                onBlur={handlePhoneBlur}
                placeholder="Mobile Number"
                containerClass={styles.phoneInputWrapper}
                inputClass={styles.phoneInput}
                buttonClass={styles.phoneInputButton}
                dropdownClass={styles.phoneInputDropdown}
                searchClass={styles.phoneInputSearch}
                containerStyle={{
                  width: "100%",
                  height: "44px",
                }}
                inputStyle={{
                  width: "100%",
                  height: "44px",
                  fontSize: "16px",
                  fontFamily: "var(--font-open-sans), sans-serif",
                  backgroundColor: "rgba(24, 6, 5, 0.5)",
                  color: "rgba(170, 168, 167, 1)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "0 8px 8px 0",
                  borderLeft: "none",
                  paddingLeft: "68px",
                  outline: "none",
                }}
                buttonStyle={{
                  backgroundColor: "rgba(24, 6, 5, 0.5)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "8px 0 0 8px",
                  borderRight: "none",
                  width: "60px",
                  padding: "0 12px 0 8px",
                  outline: "none",
                }}
                dropdownStyle={{
                  backgroundColor: "rgba(24, 6, 5, 0.95)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "8px",
                  zIndex: 10,
                  maxHeight: "200px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                }}
                searchStyle={{
                  backgroundColor: "rgba(24, 6, 5, 0.8)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "6px",
                  color: "rgba(170, 168, 167, 1)",
                  padding: "8px 12px",
                  margin: "8px",
                  fontFamily: "var(--font-open-sans), sans-serif",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
              {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                <div className={styles.errorMessage}>
                  {formik.errors.mobileNumber}
                </div>
              )}
            </div>

            <textarea
              id="message"
              name="message"
              placeholder="Message"
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.message && formik.errors.message && (
              <div className={styles.errorMessage}>{formik.errors.message}</div>
            )}

            {/* Terms and conditions checkbox */}
            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="termsAccepted"
                name="termsAccepted"
                checked={formik.values.termsAccepted}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <label htmlFor="termsAccepted" className={styles.termsLabel}>
                <span>By submitting, I agree with&nbsp;</span>
                <Link className="hyperlink" href="/terms-and-conditions">
                  <span className="underline">Terms & Conditions</span>
                </Link>
              </label>
            </div>
            {formik.touched.termsAccepted && formik.errors.termsAccepted && (
              <div className={styles.errorMessage}>
                {formik.errors.termsAccepted}
              </div>
            )}

            {/* reCAPTCHA */}
            {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
              <div className={styles.recaptchaContainer}>
                <ReCAPTCHA
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                  onChange={(value) => {
                    formik.setFieldValue("recaptcha", value);
                  }}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.recaptcha && formik.errors.recaptcha && (
                  <div className={styles.errorMessage}>
                    {formik.errors.recaptcha}
                  </div>
                )}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={
                isLoading ||
                !formik.isValid ||
                !formik.values.termsAccepted ||
                (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY &&
                  !formik.values.recaptcha)
              }
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
        {/* Warehouses Container */}
        <div className={styles.warehousesContainer}>
          {/* Tabs */}
          <div className={styles.tabsContainer}>
            {tabs.map((tab) => (
              <div
                key={tab.id}
                ref={(el) => (tabRefs.current[tab.id] = el)}
                className={`${styles.tab} ${
                  selectedTab === tab.value ? styles.active : ""
                }`}
                onClick={() => handleTabClick(tab.value)}
              >
                {tab.label}
              </div>
            ))}
            {/* Moving Indicator */}
            <div className={styles.indicator} style={indicatorStyle} />
          </div>

          {/* Tab Content */}
          {selectedTab === "Office" && (
            <div className={styles.tabContentContainer}>
              {/* Image */}
              <a
                className={styles.imageContainer}
                href="https://maps.app.goo.gl/3t6bo6xZssmdb6tE6"
                target="_blank"
              >
                <img
                  className={styles.image}
                  src={getCDNImageUrl("officeLocation.webp")}
                  alt="Office"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </a>
              {/* Mobile And Address Container */}
              <div className={styles.mobileAddressContainer}>
                <div className={styles.infoCard}>
                  {/* Icon */}
                  <MobileNumberSVG />
                  {/* Heading And Detail */}
                  <div className={styles.infoContent}>
                    <div className={styles.infoHeading}>Contact Details:</div>
                    <div className={styles.infoDetail}>+1 (908) 663 8750</div>
                    <div className={styles.infoDetail}>info@elchemy.com</div>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  {/* Icon */}
                  <AddressSVG />
                  {/* Heading And Detail */}
                  <div className={styles.infoContent}>
                    <div className={styles.infoHeading}>Address:</div>
                    <div className={styles.infoDetail}>
                      100 Somerset Corporate Blvd, Bridgewater, NJ 08807, USA
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedTab === "Warehouse 1" && (
            <div className={styles.tabContentContainer}>
              {/* Image */}
              <a
                className={styles.imageContainer}
                href="https://maps.app.goo.gl/KypfuUV7ipbL7KFj8"
                target="_blank"
              >
                <img
                  className={styles.image}
                  src={getCDNImageUrl("Primary-Warehouse.webp")}
                  alt="Office"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </a>
              {/* Mobile And Address Container */}
              <div className={styles.mobileAddressContainer}>
                <div className={styles.infoCard}>
                  {/* Icon */}
                  <MobileNumberSVG />
                  {/* Heading And Detail */}
                  <div className={styles.infoContent}>
                    <div className={styles.infoHeading}>Contact Details:</div>
                    <div className={styles.infoDetail}>+1 (908) 663 8782</div>
                    <div className={styles.infoDetail}>info@elchemy.com</div>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  {/* Icon */}
                  <AddressSVG />
                  {/* Heading And Detail */}
                  <div className={styles.infoContent}>
                    <div className={styles.infoHeading}>Address:</div>
                    <div className={styles.infoDetail}>
                      18 Van Veghten Drive, Bridgewater, NJ 08807
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedTab === "Warehouse 2" && (
            <div className={styles.tabContent}>
              <div className={styles.placeholderContent}>
                <h3>Warehouse 2</h3>
                <p>Warehouse 2 information will be displayed here.</p>
              </div>
            </div>
          )}
          {/* 
          {selectedTab === "Warehouse 3" && (
            <div className={styles.tabContent}>
              <div className={styles.placeholderContent}>
                <h3>Warehouse 3</h3>
                <p>Warehouse 3 information will be displayed here.</p>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
}

export default ContactUsWithWarehouses;
